import React from 'react';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { PhotoCounterBar } from './PhotoNavArrows';

// The single "click a photo to see it bigger" dialog shared by PhotoCell (grid cells) and
// PropertyRecordCard (map), so both present the same enlarged-photo experience.
const PhotoDetailDialog = ({
  open,
  onClose,
  onExited,
  photoUrl,
  imgLoading = false,
  onImgLoad,
  onImgError,
  nav, // { onPrev, onNext, current, total } — pass whenever there's an MLS carousel to page through
  emptyState, // node rendered instead of the <img> when there's no photoUrl yet
  dragActive = false,
  dragHandlers = {},
  primaryAction, // { label, onClick, disabled }
  secondaryAction, // { label, onClick, disabled } — gray, pinned to the bottom left
  dialogProps,
}) => (
  <StableMuiDialog open={open} onClose={onClose} onExited={onExited} maxWidth="md" fullWidth {...dialogProps}>
    <DialogTitle>Photo Preview</DialogTitle>
    <DialogContent>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Fixed-height box so opening the dialog, switching MLS photos, or the loading
            spinner never resizes the dialog — only what's drawn inside it changes. */}
        <div
          style={{
            width: '100%',
            height: '70vh',
            minHeight: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
          {...dragHandlers}
        >
          {imgLoading && (
            <CircularProgress
              size={40}
              style={{
                position: 'absolute',
                zIndex: 1,
              }}
            />
          )}
          {photoUrl ? (
            // No `key` here on purpose: swapping `src` on the same <img> lets the browser keep
            // showing the previous photo until the next one decodes, instead of flashing blank.
            <img
              src={photoUrl}
              alt="Enlarged Property"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                opacity: imgLoading ? 0.3 : 1,
                transition: 'opacity 0.2s ease',
              }}
              onLoad={onImgLoad}
              onError={onImgError}
            />
          ) : (
            <div style={{ width: '100%', height: '100%' }}>{emptyState}</div>
          )}
          {dragActive && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(25, 118, 210, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
              }}
            >
              <div style={{ color: '#1976d2', fontWeight: 500, background: '#fff', padding: '10px 20px', borderRadius: 4 }}>
                Drop photo to upload
              </div>
            </div>
          )}
        </div>
        {nav && nav.total > 1 && (
          <PhotoCounterBar onPrev={nav.onPrev} onNext={nav.onNext} current={nav.current} total={nav.total} />
        )}
      </div>
    </DialogContent>
    <DialogActions>
      {secondaryAction && (
        <Button
          onClick={secondaryAction.onClick}
          disabled={secondaryAction.disabled}
          variant="outlined"
          sx={{
            mr: 'auto',
            color: 'grey.500',
            borderColor: 'grey.400',
            '&:hover': { borderColor: 'grey.600', backgroundColor: 'grey.50', color: 'grey.700' },
          }}
        >
          {secondaryAction.label}
        </Button>
      )}
      {primaryAction && (
        <Button
          onClick={primaryAction.onClick}
          disabled={primaryAction.disabled}
          variant="contained"
          startIcon={<CloudUploadIcon />}
          sx={{ px: 2 }}
        >
          {primaryAction.label}
        </Button>
      )}
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </StableMuiDialog>
);

export default PhotoDetailDialog;
